import React from 'react';
import { useCMS } from '../context/CMSContext';
import { TrendingUp, BookCheck, ShieldAlert, HeartHandshake, ArrowRight, Globe, Target, CheckCircle2, Quote } from 'lucide-react';

const Impact = () => {
  const { content, getPageData } = useCMS();
  const pageData = getPageData('/impact');

  const c = (key, fallback) => {
    if (pageData && pageData.sectionData) {
      const sections = ['header', 'spheres', 'stats', 'sdg', 'testimonial', 'cta'];
      for (const type of sections) {
        if (key.includes(`_${type}_`)) {
          const field = key.split(`_${type}_`)[1];
          const val = pageData.sectionData[type]?.[field];
          if (val) return val;
        }
      }
    }
    return content[key] || fallback;
  };

  return (
    <div className="animate-in fade-in duration-1000 bg-brandWhite">
      
      {/* 1. Impact Hero - DARK THEME */}
      <section className="relative min-h-[70vh] flex items-center pt-20 overflow-hidden bg-brandBlack">
        <div className="absolute inset-0 dot-pattern opacity-10"></div>
        <div className="absolute top-0 right-0 w-1/2 h-full bg-brandGreen/20 organic-shape blur-3xl -z-0"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center space-x-2 text-brandGreen font-bold tracking-[0.3em] text-xs uppercase">
                <div className="w-8 h-[1px] bg-brandGreen"></div>
                <span>{c('impact_header_eyebrow', 'Evidence of Change')}</span>
              </div>
              <h1 className="text-6xl md:text-8xl font-bold text-brandWhite leading-none tracking-tighter">
                Impact <br/>
                <span className="text-brandGreen italic font-light">Defined.</span>
              </h1>
              <p className="text-xl text-brandWhite/60 leading-relaxed max-w-md">
                We measure our success not by the number of projects completed, but by the dignity restored and the systems changed.
              </p>
            </div>
            
            <div className="relative">
              <div className="relative z-10 rounded-[3rem_1rem_3rem_1rem] overflow-hidden shadow-2xl aspect-video lg:aspect-square">
                <img 
                  className="w-full h-full object-cover" 
                  alt="Impact Hero" 
                  src="https://images.unsplash.com/photo-1524069290683-0457abfe42c3?q=80&w=2070&auto=format&fit=crop" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brandBlack/60 via-transparent to-transparent"></div>
              </div>
              <div className="absolute -bottom-8 -left-4 sm:-left-8 bg-brandGreen p-6 sm:p-8 rounded-2xl sm:rounded-3xl shadow-2xl animate-float z-20">
                <p className="text-3xl sm:text-4xl font-bold text-brandWhite">15+</p>
                <p className="text-[8px] sm:text-[10px] uppercase tracking-widest font-bold text-brandWhite/70">Rural Districts Reached</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. SDG Alignment Row */}
      <section className="py-12 bg-brandBeige/50 border-b border-brandBeige">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left gap-8">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-brandWhite rounded-xl shadow-sm">
                <Globe className="w-6 h-6 text-brandGreen" />
              </div>
              <p className="font-bold text-brandBlack/80">{c('impact_sdg_point1', 'Global Alignment with 7 UN SDGs')}</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-brandWhite rounded-xl shadow-sm">
                <Target className="w-6 h-6 text-brandMauve" />
              </div>
              <p className="font-bold text-brandBlack/80">{c('impact_sdg_point2', 'Locally Led Intervention Design')}</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-brandWhite rounded-xl shadow-sm">
                <CheckCircle2 className="w-6 h-6 text-brandGreen" />
              </div>
              <p className="font-bold text-brandBlack/80">{c('impact_sdg_point3', 'Transparent Financial Reporting')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Core Spheres Section */}
      <section className="py-32 bg-brandWhite">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-24">
             <h2 className="text-sm font-bold uppercase tracking-[0.4em] text-brandMauve mb-6">Our Core Spheres</h2>
             <h3 className="text-5xl font-bold">{c('impact_spheres_title', 'Multidimensional Change')}</h3>
          </div>

          <div className="space-y-32">
            {/* Sphere 1 */}
            <SphereRow 
              image={c('impact_spheres_sphere1_image', 'https://images.unsplash.com/photo-1590650516494-2c8e4a446d54?q=80&w=2070&auto=format&fit=crop')}
              icon={<TrendingUp className="w-10 h-10" />}
              subtitle={c('impact_spheres_sphere1_subtitle', 'Livelihoods & Agribusiness')}
              title={c('impact_spheres_sphere1_title', 'Economic Empowerment')}
              desc={c('impact_spheres_sphere1_desc', 'We enable individuals and groups to build sustainable livelihoods by improving access to skills, resources, and financial opportunities.')}
              stats={[
                c('impact_spheres_sphere1_stat1', 'Increased household income by 40%'),
                c('impact_spheres_sphere1_stat2', '13 active women-led group enterprises'),
                c('impact_spheres_sphere1_stat3', 'Robust financial inclusion & savings culture')
              ]}
              reverse={false}
              accent="brandGreen"
              bgHover="bg-brandGreen/5"
            />

            {/* Sphere 2 */}
            <SphereRow 
              image={c('impact_spheres_sphere2_image', 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?q=80&w=2073&auto=format&fit=crop')}
              icon={<BookCheck className="w-10 h-10" />}
              subtitle={c('impact_spheres_sphere2_subtitle', 'Holistic Learning Models')}
              title={c('impact_spheres_sphere2_title', 'Education & Skill Access')}
              desc={c('impact_spheres_sphere2_desc', 'We expand access to quality education and practical skills for children, youth, and women who face systemic barriers.')}
              stats={[
                c('impact_spheres_sphere2_stat1', '310+ children supported annually'),
                c('impact_spheres_sphere2_stat2', 'Vocational pathways for out-of-school youth'),
                c('impact_spheres_sphere2_stat3', '100% PLE First Grade performance')
              ]}
              reverse={true}
              accent="brandMauve"
              bgHover="bg-brandMauve/10"
            />

            {/* Sphere 3 */}
            <SphereRow 
              image={c('impact_spheres_sphere3_image', 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?q=80&w=2070&auto=format&fit=crop')}
              icon={<ShieldAlert className="w-10 h-10" />}
              subtitle={c('impact_spheres_sphere3_subtitle', 'Humanitarian Response')}
              title={c('impact_spheres_sphere3_title', 'Community Resilience')}
              desc={c('impact_spheres_sphere3_desc', 'We support communities to withstand and recover from social, economic, and humanitarian shocks.')}
              stats={[
                c('impact_spheres_sphere3_stat1', 'Rapid crisis response systems'),
                c('impact_spheres_sphere3_stat2', 'Vulnerability reduction programs'),
                c('impact_spheres_sphere3_stat3', 'Stronger local community governance')
              ]}
              reverse={false}
              accent="red-500"
              bgHover="bg-red-50"
            />

            {/* Sphere 4 */}
            <SphereRow 
              image={c('impact_spheres_sphere4_image', 'https://images.unsplash.com/photo-1531206715517-5cd94ee91962?q=80&w=2070&auto=format&fit=crop')}
              icon={<HeartHandshake className="w-10 h-10" />}
              subtitle={c('impact_spheres_sphere4_subtitle', 'Social Innovation')}
              title={c('impact_spheres_sphere4_title', 'Women & Youth Leadership')}
              desc={c('impact_spheres_sphere4_desc', 'We invest in women and young people as drivers of change. By building leadership capacity and confidence.')}
              stats={[
                c('impact_spheres_sphere4_stat1', 'Leadership training for 600+ women'),
                c('impact_spheres_sphere4_stat2', 'Youth-driven social enterprise incubator'),
                c('impact_spheres_sphere4_stat3', 'Inclusive community policy advocacy')
              ]}
              reverse={true}
              accent="purple-600"
              bgHover="bg-purple-50"
            />
          </div>
        </div>
      </section>

      {/* 4. Numerical Stats Section */}
      <section className="py-32 bg-brandBlack text-brandWhite relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-brandGreen/10 organic-shape blur-3xl opacity-50"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-4">
              <h2 className="text-4xl font-bold leading-tight">The Pulse of <br/><span className="text-brandGreen">Our Impact.</span></h2>
              <p className="mt-6 text-brandWhite/40">Data driven results from 30 years of on-ground implementation.</p>
            </div>
            
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-8">
              <StatItem num={c('impact_stats_enroll_num', '310+')} label={c('impact_stats_enroll_label', 'Annual School Enrollments')} color="text-brandGreen" />
              <StatItem num={c('impact_stats_groups_num', '13')} label={c('impact_stats_groups_label', 'Women-led Business Groups')} color="text-brandMauve" />
              <StatItem num={c('impact_stats_indirect_num', '600+')} label={c('impact_stats_indirect_label', 'Indirect Beneficiaries')} color="text-brandGreen" />
              <StatItem num={c('impact_stats_loans_num', '187')} label={c('impact_stats_loans_label', 'Higher-Ed EduLoans Issued')} color="text-brandMauve" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Quote */}
      <section className="py-40 bg-brandWhite relative overflow-hidden">
        <div className="absolute left-0 bottom-0 w-64 h-64 bg-brandMauve/5 organic-shape -z-0"></div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <Quote className="w-16 h-16 text-brandBeige mx-auto mb-10" />
          <h2 className="text-3xl md:text-5xl font-light leading-snug italic text-brandBlack/80" dangerouslySetInnerHTML={{__html: c('impact_testimonial_quote', '"Impact is defined by <span class="text-brandGreen font-bold">lasting change</span>, not short-term outputs. We focus on strengthening people, systems, and communities."')}}>
          </h2>
          <div className="mt-12">
            <p className="font-bold text-lg">{c('impact_testimonial_name', 'Eunice Acan')}</p>
            <p className="text-xs uppercase tracking-widest text-brandBlack/40 font-bold">{c('impact_testimonial_role', 'CEO, Eunikare International')}</p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 pb-20">
        <div className="max-w-7xl mx-auto bg-brandBeige rounded-[4rem] p-12 md:p-20 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full dot-pattern opacity-10"></div>
          <div className="relative z-10">
            <h2 className="text-4xl md:text-6xl font-bold mb-8">{c('impact_cta_title', 'Ready to grow with us?')}</h2>
            <p className="text-xl text-brandBlack/60 max-w-2xl mx-auto mb-12">
              {c('impact_cta_subtitle', 'Join us in designing and delivering solutions that restore dignity and build resilient communities across Uganda.')}
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <a className="bg-brandGreen text-brandWhite px-12 py-5 rounded-full font-bold hover:bg-brandBlack shadow-xl transition-all" href="#/partnership">Become a Partner</a>
              <button className="bg-brandBlack text-brandWhite px-12 py-5 rounded-full font-bold hover:bg-brandGreen shadow-xl transition-all">Support our Programs</button>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

const SphereRow = ({ image, icon, subtitle, title, desc, stats, reverse, accent, bgHover }) => {
  const accentTextClass = {
    'brandGreen': 'text-brandGreen',
    'brandMauve': 'text-brandMauve',
    'red-500': 'text-red-500',
    'purple-600': 'text-purple-600'
  };
  const colorClass = accentTextClass[accent] || 'text-brandGreen';

  return (
    <div className={`flex flex-col lg:flex-row items-center gap-16 ${reverse ? 'lg:flex-row-reverse' : ''}`}>
      <div className="w-full lg:w-1/2 relative group">
        <div className={`absolute -inset-4 ${bgHover} rounded-[4rem] group-hover:inset-0 transition-all duration-700`}></div>
        <div className="relative rounded-[3rem] overflow-hidden shadow-xl aspect-video lg:aspect-[4/3]">
          <img className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" alt={title} src={image} />
        </div>
        <div className={`absolute -top-6 -right-6 w-20 h-20 bg-brandWhite rounded-2xl shadow-2xl flex items-center justify-center ${colorClass} border border-brandBeige z-10`}>
          {icon}
        </div>
      </div>
      
      <div className="w-full lg:w-1/2 space-y-8">
        <div className="space-y-2">
          <p className={`text-xs font-bold uppercase tracking-[0.3em] ${colorClass}`}>{subtitle}</p>
          <h4 className="text-4xl font-bold leading-tight">{title}</h4>
        </div>
        <p className="text-lg text-brandBlack/60 leading-relaxed">{desc}</p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {stats.map((s, i) => (
            <div key={i} className="flex items-start space-x-3 bg-brandBeige/40 p-4 rounded-2xl border border-brandBeige/50">
              <CheckCircle2 className={`w-5 h-5 shrink-0 mt-0.5 ${colorClass}`} />
              <span className="text-sm font-medium text-brandBlack/80 leading-snug">{s}</span>
            </div>
          ))}
        </div>
        
        <div className="pt-4">
          <a className="inline-flex items-center font-bold group" href="#/partnership">
            <span className="underline underline-offset-8 decoration-2 decoration-brandGreen mr-4">Support this Pillar</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
          </a>
        </div>
      </div>
    </div>
  );
};

const StatItem = ({ num, label, color }) => (
  <div className="p-8 border border-brandWhite/10 rounded-[2.5rem] bg-brandWhite/5 hover:bg-brandWhite/10 transition-all">
    <h5 className={`text-6xl font-bold mb-2 tabular-nums ${color}`}>{num}</h5>
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-brandWhite/60">{label}</p>
  </div>
);

export default Impact;

